import { mdiFan } from '@mdi/js'
import type { PictorialBarSeriesOption } from 'echarts/charts'
import type { GridComponentOption } from 'echarts/components'
import type { ComposeOption } from 'echarts/core'
import * as zod from 'zod'
import type { DepartmentPageLine } from '../Device'
import { COMMON_WIDGET_DATA_CONSTRUCTOR, COMMON_WIDGET_ELEMENT_DATA, PICTOGRAPH, WidgetElementSchema, WidgetSchema, type AbstractWidgetData, type BaseWidgetElementDialogProps, type GraphWidgetElementData, type PartialWidget, type PartialWidgetElement, type WidgetConstructor } from './Common'
import type { BaseWidgetDataRequest, WidgetDataResponse } from './Widgets'
import type { SetRequiredDeep } from 'type-fest'

export type PictographChartOptions = ComposeOption<
  PictorialBarSeriesOption
  | GridComponentOption
>
export type PictographWidgetDataRequest = BaseWidgetDataRequest & {
  type: 'PICTOGRAPH'
  widget: PictographWidget
}

export type PictographWidgetOptionsResponse = WidgetDataResponse & {
  type: 'PICTOGRAPH'
  options: PictographChartOptions
}

export const PictographWidgetElementSchema = WidgetElementSchema.extend({
  type: PICTOGRAPH,
  symbol: zod.string().nullable(),
  color: zod.string().nullable(),
  backgroundColor: zod.string().nullable(),
  group: zod.number(),
})
export type PictographWidgetElement = zod.infer<typeof PictographWidgetElementSchema>
export type PictographWidgetElementPartial = SetRequiredDeep<PartialWidgetElement<PictographWidgetElement>, 'type'>

export const PictographWidgetSchema = WidgetSchema.extend({
  type: PICTOGRAPH,
  defaultSymbol: zod.string(),
  defaultColor: zod.string(),
  defaultBackgroundColor: zod.string(),
  min: zod.number(),
  max: zod.number(),
  showAxis: zod.boolean(),
  showAverageValue: zod.boolean(),
  showLowestValue: zod.boolean(),
  showHighestValue: zod.boolean(),
  showCurrentValue: zod.boolean(),
  enableLineEdit: zod.boolean(),
  elements: PictographWidgetElementSchema.array(),
})
export type PictographWidget = zod.infer<typeof PictographWidgetSchema>
export type PictographWidgetPartial = SetRequiredDeep<PartialWidget<PictographWidget>, 'type' | 'elements' | `elements.${number}.type`>

export interface PictographWidgetElementData extends GraphWidgetElementData {
  symbol: string
  backgroundColor: string
  group: string
  readOnly: boolean
}
export interface PictographWidgetData extends AbstractWidgetData<PictographWidgetElementData> {
  type: 'PICTOGRAPH'
  defaultSymbol: string
  defaultColor: string
  defaultBackgroundColor: string
  min: number
  max: number
  showAxis: boolean
  showAverageValue: boolean
  showLowestValue: boolean
  showHighestValue: boolean
  showCurrentValue: boolean
  enableLineEdit: boolean
  elements: PictographWidgetElementData[]
}
export interface PictographWidgetElementDialogProps extends BaseWidgetElementDialogProps {
  type: 'PICTOGRAPH'
  line: DepartmentPageLine
}
export const DEFAULT_PICTOGRAPH_WIDGET_ELEMENT: PictographWidgetElementPartial = {
  ...COMMON_WIDGET_ELEMENT_DATA,
  symbol: null,
  color: null,
  backgroundColor: null,
  group: 0,
  type: 'PICTOGRAPH',
}
export const PICTOGRAPH_WIDGET_CONSTRUCTOR: WidgetConstructor<PictographWidgetPartial> = () => ({
  type: 'PICTOGRAPH',
  ...COMMON_WIDGET_DATA_CONSTRUCTOR(),
  min: 0,
  max: 1,
  defaultSymbol: mdiFan,
  defaultColor: '#800000FF',
  defaultBackgroundColor: '#dededfFF',
  showAverageValue: false,
  showLowestValue: false,
  showHighestValue: false,
  showCurrentValue: false,
  enableLineEdit: false,
  showAxis: true,
  elements: [DEFAULT_PICTOGRAPH_WIDGET_ELEMENT],
})
